export function formatAgeFirstAttack(age: string): string {
  switch (age) {
    case '0-10 år':
      return 'Huvudvärken debuterade vid 0-10 års ålder.';
    case '11-20 år':
      return 'Huvudvärken debuterade vid 11-20 års ålder.';
    case '21-30 år':
      return 'Huvudvärken debuterade vid 21-30 års ålder.';
    case '31-40 år':
      return 'Huvudvärken debuterade vid 31-40 års ålder.';
    case '41-50 år':
      return 'Huvudvärken debuterade vid 41-50 års ålder.';
    case 'mer än 50 år':
      return 'Huvudvärken debuterade då patienten var äldre än 50 år.';
    default:
      return '';
  }
}

export function formatHeadacheDuration(duration: string): string {
  switch (duration) {
    case 'mindre än 1 timme':
      return 'Ett obehandlat anfall varar mindre än 1 timme.';
    case '1-3 timmar':
      return 'Ett obehandlat anfall varar ca 1-3 timmar.';
    case '4-72 timmar':
      return 'Ett obehandlat anfall varar ca 4-72 timmar.';
    case 'mer än 72 timmar':
      return 'Ett obehandlat anfall varar mer än 72 timmar.';
    default:
      return duration;
  }
}

export function formatPainLocation(location: string): string {
  switch (location) {
    case 'i hela huvudet':
      return 'Smärtan är vanligtvis lokaliserad till hela huvudet';
    case 'i halva huvudet':
      return 'Smärtan är vanligtvis lokaliserad till halva huvudet';
    case 'som ett band runt huvudet':
      return 'Smärtan är vanligtvis lokaliserad som ett band runt huvudet';
    case 'i nacken/bakhuvudet':
      return 'Smärtan är vanligtvis lokaliserad till nacken/bakhuvudet';
    case 'i en specifik punkt eller litet område av huvudet':
      return 'Smärtan är vanligtvis lokaliserad till en specifik punkt eller litet område av huvudet';
    case 'i eller runt ena ögat eller i tinningen nära ena ögat':
      return 'Smärtan är vanligtvis lokaliserad i eller runt ena ögat eller i tinningen nära ena ögat.';
    default:
      return location;
  }
}

export function formatAuraSymptoms(symptoms: string[]): string {
  if (!Array.isArray(symptoms) || symptoms.length === 0) return '';
  if (symptoms.includes('Inget av ovanstående')) return 'Förnekar aura.';

  const descriptions: string[] = [];

  symptoms.forEach((symptom) => {
    switch (symptom) {
      case 'synstörning (flimmer/blixtar i delar av synfältet)':
        descriptions.push('Har synstörning');
        break;
      case 'känselstörning (domning/stickning i ena sidans arm och/eller ben)':
        descriptions.push('Har känselstörning');
        break;
      case 'svårighet att hitta ord':
        descriptions.push('Har svårighet att hitta ord');
        break;
      case 'sluddrigt tal':
        descriptions.push('Har sluddrigt tal');
        break;
      case 'svaghet i ena sidans arm och/eller ben':
        descriptions.push('Har svaghet i ena sidans arm och/eller ben');
        break;
      case 'yrsel':
        descriptions.push('Har yrsel');
        break;
      case 'hängande ögonlock och/eller mindre pupill på samma sida som huvudvärken':
        descriptions.push(
          'Har hängande ögonlock och/eller mindre pupill på samma sida som huvudvärken'
        );
        break;
      case 'svullet ögonlock på samma sida som huvudvärken':
        descriptions.push('Har svullet ögonlock på samma sida som huvudvärken');
        break;
      case 'ögonrodnad och/eller ökat tårflöde på samma sida som huvudvärken':
        descriptions.push(
          'Har ögonrodnad och/eller ökat tårflöde på samma sida som huvudvärken'
        );
        break;
      case 'rinnande näsa eller nästäppa på samma sida som huvudvärken':
        descriptions.push(
          'Har rinnande näsa eller nästäppa på samma sida som huvudvärken'
        );
        break;
      case 'svettning i pannan och ansiktet på samma sida som huvudvärken':
        descriptions.push(
          'Har svettning i pannan och ansiktet på samma sida som huvudvärken'
        );
        break;
      default:
        break;
    }
  });

  return descriptions.length > 0 ? `Aura: ${descriptions.join('. ')}.` : '';
}

export function formatPainCharacter(value: string): string {
  switch (value) {
    case 'pulserande/dunkande':
      return 'Smärtan är av pulserande/dunkande karaktär.';
    case 'molande':
      return 'Smärtan är av molande karaktär.';
    case 'skärande':
      return 'Smärtan är av skärande karaktär.';
    case 'brännande/svidande':
      return 'Smärtan är av brännande/svidande karaktär.';
    case 'huggande':
      return 'Smärtan är av huggande karaktär.';
    case 'tryckande':
      return 'Smärtan är av tryckande karaktär.';
    default:
      return value;
  }
}

export function formatPainIntensity(value: string): string {
  switch (value) {
    case 'lätt':
      return 'Som värst är den lätt.';
    case 'måttlig':
      return 'Som värst är den måttlig.';
    case 'intensiv':
      return 'Som värst är den intensiv.';
    default:
      return value;
  }
}

export function formatPreferredActionDuringAttack(value: string): string {
  switch (value) {
    case 'Lägger mig ned i ett mörkt och tyst rum':
      return 'Lägger sig ned i ett mörkt och tyst rum under pågående anfall, om möjlighet finns.';
    case 'Tar en promenad':
      return 'Tar en promenad under pågående anfall, om möjlighet finns.';
    case 'Sätter mig ned och tar det lugnt':
      return 'Sätter sig ned och tar det lugnt under pågående anfall, om möjlighet finns.';
    case 'Går omkring då smärtan är svår att stå ut med eller förvärras i liggande':
      return 'Går omkring då smärtan är svår att stå ut med eller förvärras i liggande under pågående anfall, om möjlighet finns.';
    default:
      return value;
  }
}

export function formatHeadacheDaysPerMonth(days: number): string {
  if (days === 1)
    return 'Uppskattar att det förekommer huvudvärk 1 dag per månad.';
  return `Uppskattar att det förekommer huvudvärk ${days} dagar per månad.`;
}

export function formatHeadacheFrequency(value: string) {
  if (value === 'mindre än en gång per år')
    return 'Har denna huvudvärk mindre än en gång per år';
  if (value === '1-11 gånger per år')
    return 'Har denna huvudvärk 1-11 gånger per år';
  if (value === '1-3 ggr per månad')
    return 'Har denna huvudvärk 1-3 ggr per månad';
  if (value === '4-7 ggr per månad')
    return 'Har denna huvudvärk 4-7 ggr per månad';
  if (value === '8-14 ggr per månad')
    return 'Har denna huvudvärk 8-14 ggr per månad';
  if (value === '15-29 ggr per månad')
    return 'Har denna huvudvärk 15-29 ggr per månad';
  if (value === 'dagligen') return 'Har denna huvudvärk dagligen';
  return '';
}

export function formatFrequencyDuration(value: string) {
  if (value === 'mindre än 1 månad')
    return ' sedan mindre än en månad tillbaka.';
  if (value === '1-3 månader') return ' sedan 1-3 månader.';
  if (value === '3-6 månader') return ' sedan 3-6 månader.';
  if (value === '6-12 månader') return ' sedan 6-12 månader.';
  if (value === '1-2 år') return ' sedan 1-2 år.';
  if (value === '2-5 år') return ' sedan 2-5 år.';
  if (value === '5-10 år') return ' sedan 5-10 år.';
  if (value === '10-20 år') return ' sedan 10-20 år.';
  if (value === 'mer än 20 år') return ' sedan mer än 20 år.';
  if (value === 'sedan debuten') return ' sedan debuten.';
  return '';
}

export function formatTriggersMessage(triggers: string[]): string {
  if (!Array.isArray(triggers) || triggers.length === 0) return '';

  let triggerList: string[] = [];

  triggers.forEach((condition) => {
    switch (condition) {
      case 'menstruation och/eller ägglossning':
        triggerList.push('menstruation och/eller ägglossning');
        break;
      case 'födoämne (t ex choklad, starka ostar, citrusfrukter)':
        triggerList.push('födoämne');
        break;
      case 'alkohol':
        triggerList.push('alkohol');
        break;
      case 'stress':
        triggerList.push('stress');
        break;
      case 'solljus':
        triggerList.push('solljus');
        break;
      case 'starka lukter':
        triggerList.push('starka lukter');
        break;
      case 'träning':
        triggerList.push('träning');
        break;
      case 'inget av ovanstående':
        triggerList = ['saknas'];
        break;
      default:
        break;
    }
  });

  if (triggerList.length === 0) return '';
  return triggerList[0] === 'saknas'
    ? 'Triggerfaktor saknas.'
    : `Triggerfaktor: ${triggerList.join(', ')}.`;
}

export function formatPainMedicationEffect(input: string): string {
  switch (input) {
    case 'Mycket god':
      return 'med mycket god effekt.';
    case 'God':
      return 'med god effekt.';
    case 'Måttlig':
      return 'med måttlig effekt.';
    case 'Sparsam':
      return 'med sparsam effekt';
    case 'Ingen':
      return 'Utan effekt.';
    default:
      return input;
  }
}

export function formatPainMedicationEffectiveness(input: string): string {
  switch (input.trim()) {
    case 'Alltid':
      return 'Den hjälper alltid.';
    case 'För det mesta':
      return 'Den hjälper för det mesta.';
    case 'Ibland':
      return 'Den hjälper ibland.';
    case 'Sällan':
      return 'Den hjälper sällan.';
    case 'Aldrig':
      return 'Den hjälper aldrig.';
    default:
      return input;
  }
}

export function formatPainMedicationFrequency(input: string): string {
  switch (input) {
    case 'mindre än 1 dag i veckan':
      return 'Tar denna medicin mindre än 1 dag i veckan.';
    case '1-2 dagar i veckan':
      return 'Tar denna medicin 1-2 dagar i veckan.';
    case '3-5 dagar i veckan':
      return 'Tar denna medicin 3-5 dagar i veckan.';
    case 'mer än 5 dagar i veckan':
      return 'Tar denna medicin mer än 5 dagar i veckan.';
    default:
      return input;
  }
}

export function formatPregnant(input: string): string {
  switch (input) {
    case 'yes':
      return 'OBS! Är gravid.';
    case 'no':
      return 'Ej gravid';
    case 'unsure':
      return 'Vet ej om gravid.';
    default:
      return input;
  }
}

export function formatPregnancyWeek(week: number | null | undefined): string {
  if (typeof week !== 'number') return '';
  return `Graviditetsvecka: ca ${week}.`;
}

export function formatPregnancyPlan(input: string): string {
  switch (input) {
    case 'yes':
      return 'OBS! Planerar graviditet inom 6 månader.';
    case 'no':
      return 'Planerar ej graviditet';
    default:
      return input;
  }
}

export function formatBreastfeeding(input: string): string {
  switch (input) {
    case 'yes':
      return 'OBS! Ammar.';
    case 'no':
      return 'Ammar ej.';
    default:
      return input;
  }
}
